// ===================================
// ADMIN COUPON REPORTS PAGE
// src/pages/AdminCouponReports.js
// Phase 7: Complete analytics and usage tracking
// ===================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import './AdminCouponReports.css';

const AdminCouponReports = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Report data
  const [summary, setSummary] = useState(null);
  const [topCoupons, setTopCoupons] = useState([]);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [sourceBreakdown, setSourceBreakdown] = useState({ internal: 0, external: 0 });
  const [recentUsage, setRecentUsage] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  
  // Usage log with pagination
  const [usageLog, setUsageLog] = useState([]);
  const [usagePagination, setUsagePagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  // Filters
  const [dateRange, setDateRange] = useState('30'); // days
  const [usageFilters, setUsageFilters] = useState({
    search: '',
    source: '',
    coupon_id: ''
  });

  // Fetch main reports
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/coupons/admin/reports?period=${dateRange}`);
      const data = response.data;
      
      setSummary({
        total_coupons: data.summary?.total_coupons || 0,
        active_coupons: data.summary?.active_coupons || 0,
        total_uses_all_time: data.summary?.total_uses_all_time || 0,
        total_discounts_all_time: data.summary?.total_discounts_all_time || 0,
        period_uses: data.period_stats?.usage_count || 0,
        period_discounts: data.period_stats?.total_discounts || 0,
        period_discounts_display: data.period_stats?.total_discounts_display || '$0.00',
        avg_discount: data.period_stats?.avg_discount || 0
      });
      
      setTopCoupons(data.top_coupons || []);
      setExpiringSoon(data.expiring_soon || []);
      setDailyStats(data.daily_usage || []);
      setRecentUsage(data.recent_usage || []);
      
      // Calculate source breakdown from recent usage if available
      if (data.source_breakdown) {
        setSourceBreakdown(data.source_breakdown);
      }
      
    } catch (err) {
      console.error('Fetch reports error:', err);
      setError(err.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Fetch usage log with filters
  const fetchUsageLog = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: usagePagination.page,
        limit: usagePagination.limit
      });
      
      if (usageFilters.search) params.append('search', usageFilters.search);
      if (usageFilters.source) params.append('source', usageFilters.source);
      if (usageFilters.coupon_id) params.append('coupon_id', usageFilters.coupon_id);
      
      const response = await api.get(`/coupons/admin/usage-log?${params}`);
      
      setUsageLog(response.data.usage || []);
      setUsagePagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 0
      }));
    } catch (err) {
      console.error('Fetch usage log error:', err);
    }
  }, [usagePagination.page, usagePagination.limit, usageFilters]);

  // Load data on mount
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    fetchUsageLog();
  }, [fetchUsageLog]);

  // Format helpers
  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return new Intl.NumberFormat().format(num);
  };

  // Calculate percentage for source breakdown
  const totalSourceUses = (sourceBreakdown.internal || 0) + (sourceBreakdown.external || 0);
  const internalPercent = totalSourceUses > 0 ? Math.round((sourceBreakdown.internal / totalSourceUses) * 100) : 0;
  const externalPercent = totalSourceUses > 0 ? Math.round((sourceBreakdown.external / totalSourceUses) * 100) : 0;

  if (loading) {
    return (
      <div className="admin-coupon-reports-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-coupon-reports-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">📊 Coupon Analytics</h1>
          <p className="page-subtitle">Track performance and usage metrics</p>
        </div>
        <div className="header-actions">
          <select
            className="period-select"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <Link to="/admin/coupons" className="btn btn-secondary">
            ← Back to Coupons
          </Link>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="error-banner">
          <span>⚠️</span> {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🎟️</div>
          <div className="stat-content">
            <span className="stat-value">{formatNumber(summary?.total_coupons)}</span>
            <span className="stat-label">Total Coupons</span>
          </div>
          <div className="stat-badge active">{summary?.active_coupons} active</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <span className="stat-value">{formatNumber(summary?.period_uses)}</span>
            <span className="stat-label">Uses ({dateRange} days)</span>
          </div>
          <div className="stat-sub">{formatNumber(summary?.total_uses_all_time)} all time</div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <span className="stat-value">{summary?.period_discounts_display}</span>
            <span className="stat-label">Discounts Given ({dateRange}d)</span>
          </div>
          <div className="stat-sub">{formatCurrency(summary?.total_discounts_all_time)} all time</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <span className="stat-value">{formatCurrency(summary?.avg_discount)}</span>
            <span className="stat-label">Avg Discount</span>
          </div>
          <div className="stat-sub">per transaction</div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="reports-content">
        {/* Left Column */}
        <div className="reports-column">
          {/* Source Breakdown */}
          <div className="report-card">
            <h3 className="card-title">🔗 Coupon Sources</h3>
            <div className="source-breakdown">
              <div className="source-item internal">
                <div className="source-header">
                  <span className="source-label">🏠 Internal</span>
                  <span className="source-count">{formatNumber(sourceBreakdown.internal)}</span>
                </div>
                <div className="source-bar">
                  <div 
                    className="source-fill internal" 
                    style={{ width: `${internalPercent}%` }}
                  ></div>
                </div>
                <span className="source-percent">{internalPercent}%</span>
              </div>
              
              <div className="source-item external">
                <div className="source-header">
                  <span className="source-label">🌐 External (Affiliate)</span>
                  <span className="source-count">{formatNumber(sourceBreakdown.external)}</span>
                </div>
                <div className="source-bar">
                  <div 
                    className="source-fill external" 
                    style={{ width: `${externalPercent}%` }}
                  ></div>
                </div>
                <span className="source-percent">{externalPercent}%</span>
              </div>
            </div>
          </div>

          {/* Top Coupons */}
          <div className="report-card">
            <h3 className="card-title">🏆 Top Performing Coupons</h3>
            {topCoupons.length > 0 ? (
              <table className="mini-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Uses</th>
                    <th>Discounts</th>
                  </tr>
                </thead>
                <tbody>
                  {topCoupons.slice(0, 10).map((coupon, idx) => (
                    <tr key={coupon.id || idx}>
                      <td className="rank">{idx + 1}</td>
                      <td>
                        <span className="coupon-code">{coupon.code}</span>
                      </td>
                      <td>{formatNumber(coupon.uses)}</td>
                      <td className="amount">{coupon.total_discount_display}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-message">No usage data yet</div>
            )}
          </div>

          {/* Expiring Soon */}
          <div className="report-card">
            <h3 className="card-title">⏰ Expiring Soon</h3>
            {expiringSoon.length > 0 ? (
              <div className="expiring-list">
                {expiringSoon.map((coupon, idx) => (
                  <div key={coupon.id || idx} className="expiring-item">
                    <div className="expiring-info">
                      <span className="coupon-code">{coupon.code}</span>
                      <span className="coupon-name">{coupon.name}</span>
                    </div>
                    <div className="expiring-date">
                      <span className="expires-label">Expires</span>
                      <span className="expires-value">{formatDate(coupon.expires_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-message">No coupons expiring soon</div>
            )}
          </div>
        </div>

        {/* Right Column - Usage Log */}
        <div className="reports-column wide">
          <div className="report-card full-height">
            <div className="card-header">
              <h3 className="card-title">📋 Usage Log</h3>
              <div className="log-filters">
                <input
                  type="text"
                  placeholder="Search user or code..."
                  value={usageFilters.search}
                  onChange={(e) => setUsageFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="filter-input"
                />
                <select
                  value={usageFilters.source}
                  onChange={(e) => setUsageFilters(prev => ({ ...prev, source: e.target.value }))}
                  className="filter-select"
                >
                  <option value="">All Sources</option>
                  <option value="internal">Internal</option>
                  <option value="external">External</option>
                </select>
              </div>
            </div>

            <div className="usage-log-table-wrapper">
              {usageLog.length > 0 ? (
                <table className="usage-log-table">
                  <thead>
                    <tr>
                      <th>Date/Time</th>
                      <th>User</th>
                      <th>Coupon</th>
                      <th>Source</th>
                      <th>Original</th>
                      <th>Discount</th>
                      <th>Final</th>
                      <th>Session</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageLog.map((usage, idx) => (
                      <tr key={usage.id || idx}>
                        <td className="col-date">
                          <span className="date-primary">{formatDate(usage.created_at)}</span>
                          <span className="date-time">
                            {new Date(usage.created_at).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </span>
                        </td>
                        <td className="col-user">
                          <span className="user-email">{usage.user_email || 'Unknown'}</span>
                          {usage.user_name && <span className="user-name">{usage.user_name}</span>}
                        </td>
                        <td className="col-coupon">
                          <span className="coupon-code">{usage.coupon_code}</span>
                          {usage.coupon_name && <span className="coupon-name">{usage.coupon_name}</span>}
                        </td>
                        <td className="col-source">
                          <span className={`source-badge ${usage.source || 'internal'}`}>
                            {usage.source === 'external' ? '🌐 External' : '🏠 Internal'}
                          </span>
                        </td>
                        <td className="col-amount">{formatCurrency(usage.original_amount)}</td>
                        <td className="col-discount">-{formatCurrency(usage.discount_amount)}</td>
                        <td className="col-final">{formatCurrency(usage.final_amount)}</td>
                        <td className="col-session">
                          {usage.session_id ? (
                            <Link 
                              to={`/admin/sessions?id=${usage.session_id}`}
                              className="session-link"
                              title="View Session"
                            >
                              🔗 View
                            </Link>
                          ) : (
                            <span className="no-session">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-log">
                  <span className="empty-icon">📋</span>
                  <p>No usage records found</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {usagePagination.pages > 1 && (
              <div className="log-pagination">
                <button
                  className="pagination-btn"
                  disabled={usagePagination.page === 1}
                  onClick={() => setUsagePagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  ← Previous
                </button>
                <span className="pagination-info">
                  Page {usagePagination.page} of {usagePagination.pages}
                </span>
                <button
                  className="pagination-btn"
                  disabled={usagePagination.page >= usagePagination.pages}
                  onClick={() => setUsagePagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div className="recent-activity">
        <h3 className="section-title">⚡ Recent Activity</h3>
        <div className="activity-feed">
          {recentUsage.length > 0 ? (
            recentUsage.slice(0, 10).map((activity, idx) => (
              <div key={idx} className="activity-item">
                <div className="activity-icon">🎟️</div>
                <div className="activity-content">
                  <span className="activity-user">{activity.user_email || 'A user'}</span>
                  <span className="activity-text">used</span>
                  <span className="coupon-code">{activity.coupon_code}</span>
                  <span className="activity-text">and saved</span>
                  <span className="activity-amount">{formatCurrency(activity.discount_amount)}</span>
                </div>
                <div className="activity-time">{formatDateTime(activity.created_at)}</div>
              </div>
            ))
          ) : (
            <div className="empty-activity">No recent activity</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCouponReports;